import { inject, Injectable } from '@angular/core';
import { FeatureFlagService } from '@ddv/entitlements';
import { ApiExecutorService, ApiServices } from '@ddv/http';
import { map, Observable, switchMap } from 'rxjs';

import { NewSettingsOverrides } from '../models/new-settings-overrides';
import { WidgetSettingsOverrides } from '../models/widget-settings-overrides';

@Injectable()
export class WidgetSettingsOverridesService {
    private readonly ddvApiService = inject<ApiExecutorService>(ApiServices.ddvApi);
    private readonly ddvMwService = inject<ApiExecutorService>(ApiServices.ddvMW);
    private readonly featureFlagService = inject(FeatureFlagService);

    getWidgetSettingsOverrides(
        clientCode: string,
        dashboardId: string | number,
        id: number,
    ): Observable<WidgetSettingsOverrides> {
        return this.featureFlagService.isFlagEnabled('use-aws-for-wobs', clientCode)
            .pipe(
                switchMap((isFlagEnabled) => {
                    if (isFlagEnabled) {
                        return this.ddvApiService
                            .invokeServiceWithParams<Partial<NewSettingsOverrides>>(clientCode, `dashboards/${dashboardId}/widgets/${id}/settings`)
                            .pipe(map((jsonData) => fromNewSettingsOverrides(jsonData)));
                    }

                    return this.ddvMwService
                        .invokeServiceWithParams<Partial<WidgetSettingsOverrides>>(clientCode, `dashboards/${dashboardId}/widgets/settings/${id}`);
                }),
                map((jsonData) => new WidgetSettingsOverrides(jsonData)),
            );
    }

    saveWidgetSettingsOverrides(
        clientCode: string,
        dashboardId: string | number,
        id: number,
        overrides: WidgetSettingsOverrides,
    ): Observable<void> {
        return this.featureFlagService.isFlagEnabled('use-aws-for-wobs', clientCode)
            .pipe(
                switchMap((isFlagEnabled) => {
                    if (isFlagEnabled) {
                        const newOverrides = toNewSettingsOverrides(overrides);
                        return this.ddvApiService
                            .invokeServiceWithBody<void>(clientCode, `dashboards/${dashboardId}/widgets/${id}/settings`, 'POST', newOverrides);
                    }

                    return this.ddvMwService
                        .invokeServiceWithBody<void>(clientCode, `dashboards/${dashboardId}/widgets/settings/${id}`, 'POST', overrides);
                }),
            );
    }
}

// Remove this method after the migration and sync the override names with the API ones
export function fromNewSettingsOverrides(overrides: NewSettingsOverrides): WidgetSettingsOverrides {
    return new WidgetSettingsOverrides({
        customName: overrides?.customName,
        displayNameType: overrides?.displayNameType,
        masterWidget: overrides?.isMasterWidget,
        subscribedToDashboardFilters: overrides?.isSubscribedToDashboardFilters,
        filterToActiveDateNotRange: overrides?.filterToActiveDateNotRange,
        defaultVisualizationType: overrides?.defaultVisualizationType,
        defaultSlicerForDefaultVisualization: overrides?.defaultSlicerForDefaultVisualization,
        defaultSortOnForDefaultVisualization: overrides?.defaultSortOnForDefaultVisualization,
        defaultSortDirectionForDefaultVisualization: overrides?.defaultSortDirectionForDefaultVisualization,
        realtimeUpdates: overrides?.realtimeUpdates,
    });
}

// Remove this method after the migration and sync the override names with the API ones
export function toNewSettingsOverrides(overrides: WidgetSettingsOverrides): NewSettingsOverrides {
    return {
        customName: overrides.customName ?? undefined,
        displayNameType: overrides.displayNameType,
        isMasterWidget: overrides.masterWidget,
        isSubscribedToDashboardFilters: overrides.subscribedToDashboardFilters,
        filterToActiveDateNotRange: overrides.filterToActiveDateNotRange,
        defaultVisualizationType: overrides.defaultVisualizationType ?? undefined,
        defaultSlicerForDefaultVisualization: overrides.defaultSlicerForDefaultVisualization ?? undefined,
        defaultSortOnForDefaultVisualization: overrides.defaultSortOnForDefaultVisualization ?? undefined,
        defaultSortDirectionForDefaultVisualization: overrides.defaultSortDirectionForDefaultVisualization ?? undefined,
        realtimeUpdates: overrides.realtimeUpdates ?? undefined,
    };
}
